import React, { useState, useEffect } from 'react';
import { X, Upload, Image as ImageIcon, Check, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../App';

function ImagePicker({ value, onChange, label, previewHeight = "h-40" }) {
  const [showModal, setShowModal] = useState(false);
  const [mediaImages, setMediaImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedImage, setSelectedImage] = useState(value || '');

  // Fetch media images when modal opens
  useEffect(() => {
    if (showModal) {
      fetchMediaImages();
    }
  }, [showModal]);

  const fetchMediaImages = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/media`);
      // Filter only images (not videos)
      const images = response.data.filter(item => item.type === 'image');
      setMediaImages(images);
    } catch (error) {
      console.error('Failed to fetch media:', error);
      toast.error('Failed to load media library');
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    setUploading(true);
    const token = localStorage.getItem('token');
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/upload-image`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      const newImageUrl = response.data.url;
      setSelectedImage(newImageUrl);
      onChange(newImageUrl);
      toast.success('Image uploaded successfully!');
      setShowModal(false);
      
      // Refresh media list
      fetchMediaImages();
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Session expired. Please log in again.');
      } else {
        toast.error('Failed to upload image');
      }
    } finally {
      setUploading(false);
    }
  };

  const handleSelectImage = (imageUrl) => {
    setSelectedImage(imageUrl);
  };

  const handleConfirmSelection = () => {
    onChange(selectedImage);
    setShowModal(false);
  };

  const handleClearImage = () => {
    setSelectedImage('');
    onChange('');
  };

  return (
    <div className="space-y-3">
      {/* Label */}
      {label && (
        <label className="block font-semibold" style={{ color: '#1e3a8a' }}>
          {label}
        </label>
      )}

      {/* Current Image Preview & Actions */}
      <div className="flex gap-4 items-start">
        {/* Preview */}
        <div className={`flex-1 bg-slate-100 rounded-lg overflow-hidden ${previewHeight}`}>
          {value ? (
            <img 
              src={value} 
              alt="Selected" 
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex flex-col items-center justify-center text-slate-400">
              <ImageIcon size={32} className="mb-2" />
              <span className="text-sm">No image selected</span>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col gap-2">
          <button
            type="button"
            onClick={() => setShowModal(true)}
            className="px-4 py-2 bg-blue-900 text-white rounded-lg hover:bg-blue-800 transition-colors flex items-center gap-2"
            data-testid="choose-image-btn"
          >
            <ImageIcon size={18} />
            Choose Image
          </button>
          {value && (
            <button
              type="button"
              onClick={handleClearImage}
              className="px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors flex items-center gap-2"
              data-testid="clear-image-btn"
            >
              <X size={18} />
              Clear
            </button>
          )}
        </div>
      </div>

      {/* URL Display (read-only) */}
      {value && (
        <div className="text-sm text-slate-500 truncate">
          <span className="font-medium">URL:</span> {value}
        </div>
      )}

      {/* Image Picker Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-slate-200">
              <h2 className="text-2xl font-bold" style={{ color: '#1e3a8a' }}>
                <ImageIcon size={28} className="inline mr-2" />
                Select or Upload Image
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="text-slate-500 hover:text-slate-700 p-2"
              >
                <X size={28} />
              </button>
            </div>

            {/* Upload Section */}
            <div className="p-6 bg-amber-50 border-b border-amber-200">
              <div className="flex items-center gap-4">
                <label className="flex-1">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                    disabled={uploading}
                  />
                  <div className="flex items-center justify-center gap-3 px-6 py-4 bg-amber-500 text-white rounded-xl cursor-pointer hover:bg-amber-600 transition-colors">
                    {uploading ? (
                      <>
                        <Loader2 size={24} className="animate-spin" />
                        <span className="font-semibold text-lg">Uploading...</span>
                      </>
                    ) : (
                      <>
                        <Upload size={24} />
                        <span className="font-semibold text-lg">Upload New Image</span>
                      </>
                    )}
                  </div>
                </label>
                <div className="text-slate-600">
                  <p className="font-medium">Or select from your Media Library below</p>
                  <p className="text-sm text-slate-500">Supports JPG, PNG, GIF, WebP</p>
                </div>
              </div>
            </div>

            {/* Media Library Grid */}
            <div className="flex-1 overflow-y-auto p-6">
              <h3 className="text-lg font-bold mb-4" style={{ color: '#1e3a8a' }}>
                Media Library ({mediaImages.length} images)
              </h3>
              
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 size={48} className="animate-spin text-blue-900" />
                </div>
              ) : mediaImages.length === 0 ? (
                <div className="text-center py-12">
                  <ImageIcon size={48} className="mx-auto mb-4 text-slate-300" />
                  <p className="text-slate-500">No images in your media library yet.</p>
                  <p className="text-slate-400 text-sm">Upload an image above to get started.</p>
                </div>
              ) : (
                <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                  {mediaImages.map((image) => (
                    <button
                      key={image.id}
                      type="button"
                      onClick={() => handleSelectImage(image.url)}
                      className={`relative aspect-square rounded-lg overflow-hidden border-4 transition-all ${
                        selectedImage === image.url 
                          ? 'border-blue-900 ring-2 ring-blue-300' 
                          : 'border-transparent hover:border-amber-300'
                      }`}
                    >
                      <img
                        src={image.url}
                        alt={image.title || 'Media image'}
                        className="w-full h-full object-cover"
                      />
                      {selectedImage === image.url && (
                        <div className="absolute inset-0 bg-blue-900 bg-opacity-40 flex items-center justify-center">
                          <div className="bg-white rounded-full p-2">
                            <Check size={24} className="text-blue-900" />
                          </div>
                        </div>
                      )}
                      {image.title && (
                        <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-60 text-white text-xs p-1 truncate">
                          {image.title}
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="p-6 border-t border-slate-200 bg-slate-50 flex items-center justify-between">
              <div className="text-sm text-slate-600">
                {selectedImage ? (
                  <span className="text-green-700 font-medium">
                    <Check size={16} className="inline mr-1" />
                    Image selected
                  </span>
                ) : (
                  <span>Select an image or upload a new one</span>
                )}
              </div>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-6 py-3 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300 transition-colors font-semibold"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleConfirmSelection}
                  disabled={!selectedImage}
                  className={`px-6 py-3 rounded-lg font-semibold transition-colors ${
                    selectedImage 
                      ? 'bg-blue-900 text-white hover:bg-blue-800' 
                      : 'bg-slate-300 text-slate-500 cursor-not-allowed'
                  }`}
                  data-testid="confirm-image-btn"
                >
                  Use Selected Image
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ImagePicker;
